/* ===========================================================================
   AL combo preview — shows the ring(s) the shopper actually configured.

   The product carries 144 per-combination renders (pearNN_rectMM.png, NN =
   Oval Stone month, MM = Rectangle Stone month). They are attached to variants,
   and the theme's `hide_variants` setting makes the gallery skip variant images
   entirely, so none of them reach the main media list — they only exist as
   hidden thumbnails (.thumbnail-list_item--variant).

   This takes the combination each bundle unit is set to and puts that render at
   the FRONT of the gallery: unit #1 becomes image 1, unit #2 image 2, and so on.
   Everything the product shipped with slides down behind them.

   Nothing happens until the shopper actually touches a stone select. On an
   untouched page every unit still reads January / January, and two red stones is
   not the picture we want leading the gallery — so the default order is left
   exactly as the theme rendered it.

   No new images are downloaded to build the index: the URLs, media ids and alt
   text are all read out of the hidden thumbnails already in the DOM.
   =========================================================================== */
(function () {
  'use strict';

  var MONTHS = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];

  /* Widths Shopify's CDN is asked for. Only ever built for <= 6 images, so a
     short ladder is plenty and keeps the srcset string small. */
  var WIDTHS = [493, 713, 990, 1206, 1426, 1946];

  var widget, gallery, viewer, viewerList, thumbList, dotList;
  var slideTemplate = null, thumbTemplateKey = null;
  var combos = {};          // "07-03" -> { target, alt, thumb }
  /* Metal-mix page only (template product.mix): the partner metal's renders,
     read from [data-al-mix-combos]. "07-03" -> CDN url. A unit whose Metal
     select is set to altMetal shows these; the slide/thumb markup is borrowed
     from the same combo in the hidden thumbnails above. */
  var altCombos = {};
  var altMetal = null;
  var dirty = {};           // "tier:unit" -> true once the shopper edits it
  var applied = '';         // signature of what is currently injected

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function monthIndex(value) {
    var i = MONTHS.indexOf(String(value || '').trim().toLowerCase());
    return i < 0 ? 0 : i + 1;
  }

  /* ---------------------------------------------------------------- index -- */

  function buildIndex() {
    var items = thumbList.querySelectorAll('.thumbnail-list__item');
    for (var i = 0; i < items.length; i++) {
      var li = items[i];
      var img = li.querySelector('img');
      if (!img) continue;
      var m = (img.getAttribute('src') || '').match(/pear(\d{2})_rect(\d{2})/);
      if (!m) continue;
      combos[m[1] + '-' + m[2]] = {
        target: li.getAttribute('data-target'),
        alt: li.getAttribute('data-alt') || '',
        thumb: li
      };
    }
    return Object.keys(combos).length > 0;
  }

  function buildAltIndex() {
    var el = widget.querySelector('[data-al-mix-combos]');
    if (!el) return;
    altMetal = el.getAttribute('data-metal');
    var urls = [];
    try { urls = JSON.parse(el.textContent); } catch (e) { return; }
    for (var i = 0; i < urls.length; i++) {
      var m = String(urls[i]).match(/pear(\d{2})_rect(\d{2})/);
      if (m) altCombos[m[1] + '-' + m[2]] = urls[i];
    }
  }

  /* "07-03" -> the theme's own combo; "alt:07-03" -> the partner-metal version
     of it (own target id, partner image, no zoom — the zoom modal only holds
     this product's media). */
  function getCombo(key) {
    if (key.indexOf('alt:') !== 0) return combos[key];
    var k = key.slice(4);
    var g = combos[k];
    return {
      target: 'alcombo-alt-' + k,
      alt: 'Verity Birthstone Duo Ring in ' + String(altMetal || '').toLowerCase(),
      thumb: g.thumb,
      src: altCombos[k].replace(/[?&]width=\d+/, ''),
      isAlt: true
    };
  }

  /* The template is cloned from a real slide so injected images inherit the
     theme's own container, ratio variables and zoom opener. Taken before
     anything is injected, and only from a plain image slide (the gallery also
     holds a GIF, which carries markup we do not want to duplicate). */
  function captureTemplate() {
    var slides = viewerList.querySelectorAll('.product__media-item');
    for (var i = 0; i < slides.length; i++) {
      var s = slides[i];
      if (s.querySelector('.product__media-toggle') && s.querySelector('img') &&
          !s.querySelector('deferred-media, product-model, video')) {
        slideTemplate = s.cloneNode(true);
        return true;
      }
    }
    return false;
  }

  /* ---------------------------------------------------------------- build -- */

  function srcFor(url, width) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function buildSlide(key, slot, total) {
    var c = getCombo(key);
    var li = slideTemplate.cloneNode(true);
    var rawId = c.target.slice(c.target.lastIndexOf('-') + 1);
    var base = c.src || c.thumb.querySelector('img').getAttribute('src').replace(/[?&]width=\d+/, '');

    li.id = 'Slide-' + c.target;
    li.setAttribute('data-media-id', c.target);
    li.setAttribute('data-alt', c.alt);
    li.setAttribute('data-al-combo-slot', String(slot));
    li.classList.remove('is-active', 'hidden');

    var imgs = li.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      img.setAttribute('src', srcFor(base, 1946));
      img.setAttribute('srcset', WIDTHS.map(function (w) {
        return srcFor(base, w) + ' ' + w + 'w';
      }).join(', '));
      img.setAttribute('sizes', '(min-width: 990px) 50vw, 100vw');
      img.setAttribute('alt', c.alt);
      /* Slot 1 is the lead image and is on screen immediately; the rest sit
         off-screen on mobile and below the fold on desktop. */
      img.setAttribute('loading', slot === 1 ? 'eager' : 'lazy');
      img.removeAttribute('width');
      img.removeAttribute('height');
    }

    /* The zoom modal already contains all 160 media, keyed on the RAW media id,
       so pointing the toggle at it is enough to make zoom work on these. */
    var toggle = li.querySelector('.product__media-toggle');
    if (toggle && c.isAlt) toggle.parentNode.removeChild(toggle);
    else if (toggle) toggle.setAttribute('data-media-id', rawId);

    var container = li.querySelector('.product-media-container') || li;
    var badge = document.createElement('span');
    badge.className = 'al-combo-badge';
    badge.textContent = total > 1 ? 'Ring ' + slot : 'Your ring';
    container.appendChild(badge);

    return li;
  }

  function buildThumb(key, slot, total) {
    var c = getCombo(key);
    var li = c.thumb.cloneNode(true);
    li.id = 'Slide-Thumbnails-alcombo-' + slot;
    if (c.isAlt) {
      li.setAttribute('data-target', c.target);
      li.setAttribute('data-alt', c.alt);
      var timgs = li.querySelectorAll('img');
      for (var t = 0; t < timgs.length; t++) {
        timgs[t].setAttribute('src', srcFor(c.src, 416));
        timgs[t].removeAttribute('srcset');
        timgs[t].setAttribute('alt', c.alt);
      }
    }
    /* `.thumbnail-list_item--variant:not(:first-child)` is what keeps the 144
       combination thumbnails out of sight. */
    li.classList.remove('thumbnail-list_item--variant', 'hidden');
    li.setAttribute('data-al-combo-slot', String(slot));

    /* The clone brings the original's descendant ids with it (the label span
       behind aria-describedby), which would duplicate ids already in the page.
       getAttribute/setAttribute rather than .id: on SVG elements .id is not
       reliably a plain string, and a thumbnail may contain inline icons. */
    var withIds = li.querySelectorAll('[id]');
    for (var i = 0; i < withIds.length; i++) {
      var prev = withIds[i].getAttribute('id') || '';
      withIds[i].setAttribute('id', prev.trim() + '-alcombo-' + slot);
    }

    var btn = li.querySelector('button');
    if (btn) {
      btn.removeAttribute('aria-current');
      btn.removeAttribute('aria-describedby');
      /* Cloned nodes carry no listeners, so this one needs its own. */
      btn.addEventListener('click', function () {
        activate(li.getAttribute('data-target'));
      });
    }

    /* Same label on the thumbnail. The thumbnail strip is the only way to tell
       the configured rings apart while scrolling the mobile gallery, and at 58px
       the renders are far too similar to read. Sits on the <li> (position:
       relative) rather than the button, which is absolutely positioned over it. */
    var badge = document.createElement('span');
    badge.className = 'al-combo-badge al-combo-badge--thumb';
    badge.textContent = total > 1 ? 'Ring ' + slot : 'Your ring';
    li.appendChild(badge);

    return li;
  }

  function buildDot(key, slot) {
    if (!dotList) return null;
    var proto = dotList.querySelector('.dot-navigation-dot');
    if (!proto) return null;
    var dot = proto.cloneNode(true);
    dot.classList.remove('active', 'hidden');
    dot.removeAttribute('aria-current');
    dot.setAttribute('data-target', getCombo(key).target);
    dot.setAttribute('data-al-combo-slot', String(slot));
    dot.addEventListener('click', function () { activate(getCombo(key).target); });
    return dot;
  }

  /* --------------------------------------------------------------- active -- */

  /* Deliberately NOT media-gallery's setActiveMedia(): that one calls
     window.scrollTo when the media sits above the viewport, which yanks the page
     out from under someone who is mid-way through the selector. Here the only
     thing that moves is the gallery's own horizontal scroll. */
  function activate(target) {
    var slide = viewerList.querySelector('[data-media-id="' + target + '"]');
    if (!slide) return;

    var all = viewerList.querySelectorAll('[data-media-id]');
    for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
    slide.classList.add('is-active');

    if (viewerList.scrollWidth > viewerList.clientWidth + 4) {
      try { viewerList.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' }); }
      catch (e) { viewerList.scrollLeft = slide.offsetLeft; }
    }

    var btns = thumbList.querySelectorAll('button[aria-current]');
    for (var j = 0; j < btns.length; j++) btns[j].removeAttribute('aria-current');
    var thumb = thumbList.querySelector('[data-target="' + target + '"]');
    if (thumb && thumb.querySelector('button')) {
      thumb.querySelector('button').setAttribute('aria-current', 'true');
    }

    if (dotList) {
      var dots = dotList.querySelectorAll('.dot-navigation-dot');
      for (var k = 0; k < dots.length; k++) dots[k].classList.remove('active');
      var dot = dotList.querySelector('.dot-navigation-dot[data-target="' + target + '"]');
      if (dot) dot.classList.add('active');
    }
  }

  /* ----------------------------------------------------------------- read -- */

  function activeTier() {
    if (widget.activeTier) {
      try { var t = widget.activeTier(); if (t) return t; } catch (e) { /* fall through */ }
    }
    var tiers = widget.querySelectorAll('.b1g2__tier');
    for (var i = 0; i < tiers.length; i++) {
      var r = tiers[i].querySelector('.b1g2__radio');
      if (r && r.checked) return tiers[i];
    }
    return null;
  }

  function selectIn(unitEl, name) {
    var sels = unitEl.querySelectorAll('.b1g2__select');
    for (var i = 0; i < sels.length; i++) {
      var n = (sels[i].getAttribute('data-option-name') ||
               sels[i].getAttribute('data-prop-name') || '').toLowerCase();
      if (n === name) return sels[i];
    }
    return null;
  }

  /* The combination each unit is currently set to, but only for units the
     shopper has actually touched. */
  function wanted() {
    var tier = activeTier();
    if (!tier) return [];
    var tierId = tier.getAttribute('data-tier') || '1';
    var units = tier.querySelectorAll('.b1g2__unit');
    var out = [];

    for (var i = 0; i < units.length; i++) {
      var unitEl = units[i];
      var unitNo = unitEl.getAttribute('data-unit') || String(i + 1);
      if (!dirty[tierId + ':' + unitNo]) continue;

      var oval = selectIn(unitEl, 'oval stone');
      var rect = selectIn(unitEl, 'rectangle stone');
      if (!oval || !rect) continue;

      var key = pad(monthIndex(oval.value)) + '-' + pad(monthIndex(rect.value));
      if (!combos[key]) continue;
      var metal = selectIn(unitEl, 'metal');
      if (metal && altMetal && metal.value === altMetal && altCombos[key]) key = 'alt:' + key;
      /* tierUnits, not out.length: a 3-ring bundle with only ring 1 configured
         still says "Ring 1", so the label does not rename itself to
         "Your ring" and back as the shopper works down the rows. */
      out.push({ slot: out.length + 1, key: key, unit: unitNo, tierUnits: units.length });
    }
    return out;
  }

  /* ---------------------------------------------------------------- apply -- */

  function clearInjected() {
    var nodes = document.querySelectorAll('[data-al-combo-slot]');
    for (var i = 0; i < nodes.length; i++) nodes[i].parentNode.removeChild(nodes[i]);
  }

  function apply(focusUnit) {
    var list = wanted();
    var signature = list.map(function (x) { return x.slot + '=' + x.key; }).join(',');
    if (signature === applied) {
      if (focusUnit) focusSlot(list, focusUnit);
      return;
    }
    applied = signature;

    clearInjected();
    if (!list.length) {
      /* Back to the theme's own order — hand the lead image back. */
      var first = viewerList.querySelector('.product__media-item');
      if (first) activate(first.getAttribute('data-media-id'));
      refreshSlider();
      return;
    }

    /* Insert in reverse so each ends up ahead of the last: slot 1 finishes at
       the very front of the gallery. */
    for (var i = list.length - 1; i >= 0; i--) {
      var item = list[i];
      viewerList.insertBefore(buildSlide(item.key, item.slot, item.tierUnits), viewerList.firstChild);
      thumbList.insertBefore(buildThumb(item.key, item.slot, item.tierUnits), thumbList.firstChild);
      var dot = buildDot(item.key, item.slot);
      if (dot) dotList.insertBefore(dot, dotList.firstChild);
    }

    refreshSlider();
    focusSlot(list, focusUnit);
  }

  function focusSlot(list, focusUnit) {
    if (!list.length) return;
    var pick = null;
    for (var i = 0; i < list.length; i++) {
      if (focusUnit && list[i].unit === focusUnit) pick = list[i];
    }
    if (!pick) pick = list[0];
    activate(getCombo(pick.key).target);
  }

  /* slider-component caches page counts and item offsets, so it has to be told
     the slide count changed or the mobile counter and swipe bounds go stale. */
  function refreshSlider() {
    if (!viewer) return;
    try {
      if (typeof viewer.initPages === 'function') viewer.initPages();
      if (typeof viewer.update === 'function') viewer.update();
    } catch (e) { /* a stale counter is not worth throwing over */ }
  }

  /* ------------------------------------------------------------------ go -- */

  function markDirty(target) {
    var unitEl = target.closest ? target.closest('.b1g2__unit') : null;
    var tierEl = target.closest ? target.closest('.b1g2__tier') : null;
    if (!unitEl || !tierEl) return null;
    var name = (target.getAttribute('data-option-name') || '').toLowerCase();
    if (name !== 'oval stone' && name !== 'rectangle stone' && name !== 'metal') return null;
    var unitNo = unitEl.getAttribute('data-unit') || '1';
    dirty[(tierEl.getAttribute('data-tier') || '1') + ':' + unitNo] = true;
    return unitNo;
  }

  function init() {
    widget = document.querySelector('b1g2-offer');
    gallery = document.querySelector('media-gallery');
    if (!widget || !gallery) return;

    viewer = gallery.querySelector('[id^="GalleryViewer"]');
    viewerList = gallery.querySelector('.product__media-list');
    thumbList = gallery.querySelector('.thumbnail-list');
    dotList = gallery.querySelector('.mobile-dot-navigation');
    if (!viewerList || !thumbList) return;

    if (!buildIndex()) return;      // not a per-combination product
    buildAltIndex();                // no-op unless this is the metal-mix page
    if (!captureTemplate()) return; // no usable slide to clone

    widget.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('b1g2__select')) return;
      var unitNo = markDirty(t);
      apply(unitNo);
    });

    /* Switching tiers swaps in a different set of unit rows (1, 3 or 6). */
    var radios = widget.querySelectorAll('.b1g2__radio');
    for (var i = 0; i < radios.length; i++) {
      radios[i].addEventListener('change', function () { apply(null); });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
